import axios from 'axios';
import { hasBundleKey, isContractKey, resolveDisplayKey, translateErrorKey } from './contract';
import { asDetailText, isDetailUserVisible } from './detailPolicy';
import { extractLegacyDetail, sanitizeKey } from './extractLegacyDetail';
import { apiErrorBodySchema } from './schema';
import { ERROR_RESOLUTION, toUserFacingMessage } from './types';
import type { ErrorResolution, ResolvedApiError } from './types';

const resolved = (
	resolution: ErrorResolution,
	messageKey: string,
	overrides: { status?: number; detail?: unknown; key?: string } = {},
	params?: Record<string, string>,
): ResolvedApiError => ({
	message: toUserFacingMessage(translateErrorKey(messageKey, params)),
	resolution,
	...overrides,
});

/**
 * Resolves any thrown value into a display-ready, Sentry-policy-aware error. Never
 * throws. Implements the 3-stage terminal cascade from the design doc:
 *
 *   0. not an AxiosError                          -> Generic
 *   1. no `response` (network / timeout)           -> Network | Timeout
 *   2. STAGE 1: new backend shape (`schema.ts`)     -> ContractKey | ContractKeyFallback
 *                                                       | ContractViolation
 *      — TERMINAL either way, never falls through to stage 2. `key` resolves through
 *        `resolveDisplayKey`'s prefix-chain fallback (ADR-2); an allow-listed `detail`
 *        (D1, `detailPolicy.ts`) is interpolated into the translated message.
 *   3. STAGE 2: legacy heuristic (`extractLegacyDetail` + `sanitizeKey`)
 *      -> LegacyKey (terminal) | LegacyUnresolved (falls through to stage 3)
 *   4. STAGE 3: `http_{status}` if present in the bundle, else `unexpected_error`.
 *
 * The whitelist check applies only to backend-derived key candidates (stages 1 and 2),
 * never to this module's own reserved-key constructions.
 */
export const resolveApiError = (error: unknown): ResolvedApiError => {
	if (!axios.isAxiosError(error)) {
		return resolved(ERROR_RESOLUTION.NonAxios, 'unexpected_error');
	}

	if (!error.response) {
		return error.code === 'ECONNABORTED'
			? resolved(ERROR_RESOLUTION.Timeout, 'timeout')
			: resolved(ERROR_RESOLUTION.Network, 'network_error');
	}

	const status = error.response.status;
	const body = error.response.data;

	// STAGE 1 — new backend shape. Terminal either way: parsing success never falls back
	// to the legacy heuristic, so contract drift is never masked by an accidental match.
	const parsed = apiErrorBodySchema.safeParse(body);
	if (parsed.success) {
		const key = parsed.data.key;
		const displayKey = resolveDisplayKey(key);

		if (displayKey === undefined) {
			return resolved(ERROR_RESOLUTION.ContractViolation, 'unexpected_error', {
				status,
				detail: parsed.data.detail,
				key,
			});
		}

		// Exact whitelist hit -> ContractKey; an ancestor (`dominio[.recurso]`) hit ->
		// ContractKeyFallback (ADR-2) — still reported (`reporting.ts`), it flags a
		// catalog gap even though the user sees real domain copy.
		const resolution =
			displayKey === key ? ERROR_RESOLUTION.ContractKey : ERROR_RESOLUTION.ContractKeyFallback;

		// D1 — allow-listed interpolation only. A non-allow-listed key (or a non-string
		// `detail`) never leaks into the rendered message; it still reaches Sentry via
		// `overrides.detail` below, untouched by this policy.
		const detailText = isDetailUserVisible(displayKey) ? asDetailText(parsed.data.detail) : undefined;
		const params = detailText === undefined ? undefined : { detail: detailText };

		return resolved(resolution, displayKey, { status, detail: parsed.data.detail, key }, params);
	}

	// STAGE 2 — legacy heuristic. `sanitizeKey` applies here only: contract keys (stage 1)
	// must match exactly, sanitizing them would hide drift.
	const candidate = extractLegacyDetail(body);
	if (candidate) {
		const sanitized = sanitizeKey(candidate);
		if (isContractKey(sanitized)) {
			return resolved(ERROR_RESOLUTION.LegacyKey, sanitized, {
				status,
				detail: candidate,
				key: candidate,
			});
		}
	}

	// STAGE 3 — status fallback. Also supplies the message when a stage-2 candidate
	// existed but wasn't whitelisted (LegacyUnresolved): the raw candidate can't be
	// trusted for translation, so it degrades to the same http_{status}/unexpected_error
	// message a candidate-less body would get, while keeping its own resolution label so
	// `shouldReport` and analytics can still tell the two apart from StatusFallback/Generic.
	const statusKey = `http_${status}`;
	const fallbackKey = hasBundleKey(statusKey) ? statusKey : 'unexpected_error';
	const resolution = candidate
		? ERROR_RESOLUTION.LegacyUnresolved
		: fallbackKey === statusKey
			? ERROR_RESOLUTION.StatusFallback
			: ERROR_RESOLUTION.Generic;

	return resolved(resolution, fallbackKey, { status, detail: candidate, key: candidate });
};
